<style scoped lang="css">
.badge-warning {
  color: #8a6915;
  background-color: rgba(255, 225, 100, 0.7);
}
</style>

<template>
  <div class="main-wrapper">
    <section class="section main-section bg-gradient-default min-vh-100 pt-3">
      <main class="container card shadow shadow-sm--hover mb-3">
        <Header title="" />
        <b-row class="mb-3">
          <b-col cols="12" class="mb-3">
            <b-button variant="default" size="sm" @click="goBack">
              <b-icon-chevron-left scale="1.2"></b-icon-chevron-left>
            </b-button>
          </b-col>
        </b-row>
        <b-row class="my-1">
          <b-col cols="12">
            <em>
              Los códigos marcados en paréntesis (XX) pueden ocurrir junto con
              una alárma de código 84 y pueden desaparecer automáticamente.
            </em>
          </b-col>
          <b-col>
            <b-input-group>
              <b-form-input
                v-model="searchKeywords"
                placeholder="Buscar código..."
                debounce="300"
              ></b-form-input>
              <b-input-group-append>
                <b-button v-if="!searchKeywords" variant="primary"
                  ><b-icon-search />
                </b-button>
                <b-button v-else variant="warning" @click="clearSearchKeywords"
                  ><b-icon-x scale="1.5" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row class="my-4" v-if="!searchKeywords">
          <b-col>
            <b-skeleton-table
              v-if="!tables.length"
              :rows="10"
              :columns="4"
              :table-props="{ striped: true }"
            />
            <b-tabs fill>
              <b-tab
                v-for="table in tables"
                :key="table.name"
                :title="table.name"
                lazy
              >
                <b-table
                  striped
                  responsive="md"
                  hover
                  :fields="table.fields"
                  :items="table.data"
                >
                  <template v-slot:cell(1)="data">
                    <b-badge :variant="badgeVariant(data)">{{
                      data | statusText
                    }}</b-badge>
                  </template>
                </b-table>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
        <b-row v-else class="my-4">
          <b-table
            striped
            responsive="md"
            hover
            :show-empty="true"
            :empty-text="emptyText"
            :fields="tableFields"
            :items="search()"
          >
            <template v-slot:cell(1)="data">
              <b-badge :variant="badgeVariant(data)">{{
                data | statusText
              }}</b-badge>
            </template>
            <template v-slot:empty="scope">
              <p>{{ scope.emptyText }}</p>
            </template>
          </b-table>
        </b-row>
      </main>
    </section>
  </div>
</template>

<script>
import * as R from "ramda";
import Header from "./Header";

function FILTER_DATA(rows) {
  return rows.filter((row) => {
    const keys = Object.values(row);
    const isHeader = keys.includes("Num") || keys.includes("Color");
    return !isHeader;
  });
}

function compareLowerCase(a, b) {
  return a.toLowerCase().includes(b.toLowerCase());
}

const FIELDS = [
  {
    key: "0",
    label: "Num",
    sortable: true,
  },
  {
    key: "1",
    label: "Color",
    sortable: true,
  },
  {
    key: "2",
    label: "Description",
  },
  {
    key: "3",
    label: "Operator Action",
  },
];

export default {
  name: "Codes",
  components: {
    Header,
  },
  data() {
    return {
      searchKeywords: "",
      emptyText: "Entrada no existe o no se ha encontrado",
      tableFields: FIELDS,
      tables: [],
    };
  },
  async mounted() {
    const [
      { default: PageOneTableOne },
      { default: PageTwoTableOne },
      { default: PageThreeTableOne },
      { default: PageThreeTableTwo },
      { default: PageFourTableOne },
      { default: PageFourTableTwo },
    ] = await Promise.all([
      import(
        /* webpackPrefetch: true */ "./data/page-one-table-one_es-MX.json"
      ),
      import(
        /* webpackPrefetch: true */ "./data/page-two-table-one_es-MX.json"
      ),
      import(
        /* webpackPrefetch: true */ "./data/page-three-table-one_es-MX.json"
      ),
      import(
        /* webpackPrefetch: true */ "./data/page-three-table-two_es-MX.json"
      ),
      import(
        /* webpackPrefetch: true */ "./data/page-four-table-one_es-MX.json"
      ),
      import(
        /* webpackPrefetch: true */ "./data/page-four-table-two_es-MX.json"
      ),
    ]);

    this.tables = [
      {
        fields: FIELDS,
        data: FILTER_DATA([...PageOneTableOne, ...PageTwoTableOne]),
        name: "Thermo King Truck, Trailer, CR, DAS, and DSR Alarm codes",
      },
      {
        fields: FIELDS,
        data: FILTER_DATA(PageThreeTableOne),
        name: "Thermo King precedent 500 Series Alarm codes",
      },
      {
        fields: FIELDS,
        data: FILTER_DATA(PageThreeTableTwo),
        name: "Precedent 600 series Yanmar diagnostic trouble codes (DTC)",
      },
      {
        fields: FIELDS,
        data: FILTER_DATA(PageFourTableOne),
        name: "Precedent 800 series Peugeot diagnostic trouble codes (DTC)",
      },
      {
        fields: FIELDS,
        data: FILTER_DATA(PageFourTableTwo),
        name: "Direct Smart Reefer (DSR) alarm codes",
      },
    ];
  },
  methods: {
    search(fields) {
      if (!this.searchKeywords || this.searchKeywords === "") return fields;

      return R.flatten(
        this.tables.map(({ data }) =>
          data.filter(
            (f) =>
              compareLowerCase(f["0"], this.searchKeywords) ||
              compareLowerCase(f["2"], this.searchKeywords) ||
              compareLowerCase(f["3"], this.searchKeywords)
          )
        )
      );
    },
    clearSearchKeywords() {
      this.searchKeywords = "";
    },
    goBack() {
      this.$router.go(-1);
    },
    badgeVariant(row) {
      const value = row.value;
      const text =
        value === "GREEN"
          ? "success"
          : value === "YELLOW"
          ? "warning"
          : "danger";
      return text;
    },
  },
  filters: {
    statusText(row) {
      const value = row.value;
      const text =
        value === "GREEN"
          ? "Correcto para funcionar"
          : value === "YELLOW"
          ? "Revise según indicado"
          : "Tome acción inmediata";
      return text;
    },
  },
};
</script>
